import React from "react";
import {
    Bar,
    CartesianGrid,
    ComposedChart,
    Line,
    ReferenceArea,
    ResponsiveContainer,
    Tooltip,
    XAxis,
    YAxis,
} from "recharts";
import { frontendErrorsFormat } from "@/features/observability/components/frontend-errors-format";
import {
    frontendErrorsSeriesModel,
    type FrontendErrorsSeriesModel,
    type FrontendErrorsSeriesRow,
} from "@/features/observability/components/frontend-errors-series-model";

type Props = {
    model: FrontendErrorsSeriesModel;
    bucketMs: number;
    rangeMs: number;
    lowerBound: boolean;
};

const WEEK_MS = 7 * 24 * 60 * 60 * 1000;
const AXIS_COLOR = "#64748B";
const GRID_COLOR = "#E6E6E6";
const DEVICES_COLOR = "#1A2128";
const GAP_COLOR = "#E7EAEE";

const describeRow = (row: FrontendErrorsSeriesRow, lowerBound: boolean): string => {
    const span = `${frontendErrorsFormat.dateTime(row.start)}–${frontendErrorsFormat.hourMinute(row.end)}`;
    const count = row.count === null ? "sin dato" : `${frontendErrorsFormat.count(row.count)} ocurrencias${lowerBound ? " (mín.)" : ""}`;
    const devices = `${frontendErrorsFormat.count(row.devices)} dispositivos`;
    const gap = row.gap ? " · hueco declarado: el valor puede ser menor que la realidad" : "";
    return `${span} · ${count} · ${devices}${gap}`;
};

export function FrontendErrorsSeriesChart({ model, bucketMs, rangeMs, lowerBound }: Props) {
    const tick = (value: number): string =>
        rangeMs >= WEEK_MS ? frontendErrorsFormat.day(value) : frontendErrorsFormat.hourMinute(value);

    return (
        <div className="fe-series">
            <div className="fe-card__hd">
                <div className="fe-card__title">{`Ocurrencias por ${frontendErrorsFormat.bucketUnit(bucketMs)}`}</div>
                <div className="fe-legend">
                    {model.keys.map((key) => {
                        const style = frontendErrorsSeriesModel.keyStyle(key);
                        return (
                            <span key={key} className="fe-legend__item">
                                <span className="fe-swatch" style={{ background: style.color }} />
                                {style.label}
                            </span>
                        );
                    })}
                    <span className="fe-legend__item">
                        <span className="fe-legend__line" />
                        Dispositivos
                    </span>
                    {model.hasGaps ? (
                        <span className="fe-legend__item">
                            <span className="fe-legend__gap" />
                            Hueco declarado
                        </span>
                    ) : null}
                </div>
            </div>
            <div className="fe-series__plot">
                <ResponsiveContainer width="100%" height="100%">
                    <ComposedChart data={model.rows} margin={{ top: 8, right: 8, left: 0, bottom: 4 }}>
                        <CartesianGrid stroke={GRID_COLOR} strokeDasharray="3 4" vertical={false} />
                        {model.rows.filter((row) => row.gap).map((row) => (
                            <ReferenceArea key={`gap-${row.start}`} x1={row.start} x2={row.start} fill={GAP_COLOR} fillOpacity={0.8} ifOverflow="extendDomain" />
                        ))}
                        <XAxis
                            dataKey="start"
                            tickFormatter={tick}
                            tick={{ fontSize: 10, fill: AXIS_COLOR }}
                            axisLine={false}
                            tickLine={false}
                            minTickGap={16}
                        />
                        <YAxis
                            allowDecimals={false}
                            tickFormatter={(value: number) => frontendErrorsFormat.count(value)}
                            tick={{ fontSize: 10, fill: AXIS_COLOR }}
                            axisLine={false}
                            tickLine={false}
                            width={48}
                        />
                        <Tooltip
                            cursor={{ fill: "rgba(28, 175, 255, 0.08)" }}
                            content={({ active, label }) => {
                                if (active !== true) return null;
                                const row = model.rows.find((candidate) => candidate.start === label);
                                if (row === undefined) return null;
                                return <div className="fe-tooltip">{describeRow(row, lowerBound)}</div>;
                            }}
                        />
                        {model.keys.map((key) => (
                            <Bar
                                key={key}
                                name={frontendErrorsSeriesModel.keyStyle(key).label}
                                dataKey={(row: FrontendErrorsSeriesRow) => row.values[key]}
                                stackId="categories"
                                fill={frontendErrorsSeriesModel.keyStyle(key).color}
                                isAnimationActive={false}
                            />
                        ))}
                        <Line
                            name="Dispositivos"
                            dataKey="devices"
                            stroke={DEVICES_COLOR}
                            strokeWidth={2}
                            dot={false}
                            connectNulls={false}
                            isAnimationActive={false}
                        />
                    </ComposedChart>
                </ResponsiveContainer>
            </div>
        </div>
    );
}
